import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, ArrowLeft, Clock, FileText, Download } from 'lucide-react';
import { useEventSchedule } from '@/hooks/useEventSchedule';
import { useQuery } from '@tanstack/react-query';
import { eventService } from '@/services/eventService';
import { supabaseErrorToToast } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { ScheduleItem } from '@/components/Events/ScheduleItem';
import { EditScheduleItemModal } from '@/components/Events/EditScheduleItemModal';
import { EventSchedule as EventScheduleType } from '@/types';
import { toast } from '@/hooks/use-toast';
import { useEventPermission } from '@/hooks/useEventPermission';

export const EventSchedule: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [newActivity, setNewActivity] = useState({ hora: '', actividad: '' });
  const [editingItem, setEditingItem] = useState<EventScheduleType | null>(null);
  const [items, setItems] = useState<EventScheduleType[]>([]);
  const { canWrite } = useEventPermission(eventId);

  // Fetch event details
  const { 
    data: event,
    isLoading: isLoadingEvent,
    error: eventError
  } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => eventId ? eventService.getEvent(eventId) : Promise.resolve(null),
    enabled: !!eventId
  });

  // Use the custom hook for schedule management
  const { 
    scheduleItems, 
    isLoading,
    createScheduleItem,
    updateScheduleItem,
    deleteScheduleItem
  } = useEventSchedule(eventId);

  // Sort items by time and update local state when scheduleItems changes
  useEffect(() => {
    if (scheduleItems) {
      // Sort by time in ascending order
      const sortedItems = [...scheduleItems].sort((a, b) => {
        // Convert time strings to comparable values
        const timeA = a.hora.split(':').map(Number);
        const timeB = b.hora.split(':').map(Number);
        
        // Compare hours first
        if (timeA[0] !== timeB[0]) {
          return timeA[0] - timeB[0];
        }
        
        // If hours are the same, compare minutes
        return timeA[1] - timeB[1];
      });
      
      setItems(sortedItems);
    }
  }, [scheduleItems]);

  // Handle event error
  useEffect(() => {
    if (eventError) {
      supabaseErrorToToast(eventError);
    }
  }, [eventError]);

  const addActivity = () => {
    if (!eventId || !newActivity.hora || !newActivity.actividad) return;
    
    createScheduleItem({
      event_id: eventId,
      hora: newActivity.hora,
      actividad: newActivity.actividad,
      orden: 0 // The orden will be ignored as we're sorting by time
    });
    
    setNewActivity({ hora: '', actividad: '' });
  };

  const handleEditItem = (id: string) => {
    const item = items.find(item => item.id === id);
    if (item) {
      setEditingItem(item);
    }
  };

  const handleSaveEdit = (data: Partial<Omit<EventScheduleType, 'id' | 'event_id'>>) => {
    if (editingItem) {
      updateScheduleItem({ 
        id: editingItem.id, 
        data 
      });
      setEditingItem(null);
    }
  };

  const removeActivity = (id: string) => {
    deleteScheduleItem(id);
  };

  // Format event date for display
  const formatEventDate = (date: Date | undefined) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }).format(date);
  };

  const handlePrint = () => {
    const escapeHtml = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const eventName = event?.nombre ? escapeHtml(event.nombre) : '';
    const eventDate = event?.fecha ? escapeHtml(formatEventDate(event.fecha)) : '';

    const rows = items
      .map(
        (item) => `
          <tr>
            <td class="hora">${escapeHtml(item.hora)}</td>
            <td class="actividad">${escapeHtml(item.actividad)}</td>
          </tr>`,
      )
      .join('');

    const html = `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Cronograma — ${eventName || 'Evento'}</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    color: #111827;
    margin: 32px;
  }
  header { border-bottom: 2px solid #111827; padding-bottom: 12px; margin-bottom: 24px; }
  .event-name { font-size: 22px; font-weight: 700; margin: 0 0 4px; }
  .event-date { font-size: 13px; color: #6b7280; margin: 0; }
  h1 { font-size: 18px; font-weight: 600; margin: 24px 0 12px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
  th { background: #f9fafb; font-weight: 600; text-transform: uppercase; font-size: 11px; letter-spacing: 0.04em; color: #374151; }
  td.hora { width: 110px; font-variant-numeric: tabular-nums; font-weight: 600; white-space: nowrap; }
  td.actividad { color: #111827; }
  .empty { padding: 24px; text-align: center; color: #6b7280; font-size: 13px; }
  @page { margin: 18mm; }
  @media print { body { margin: 0; } }
</style>
</head>
<body>
  <header>
    <p class="event-name">${eventName || 'Evento sin nombre'}</p>
    ${eventDate ? `<p class="event-date">${eventDate}</p>` : ''}
  </header>
  <h1>Cronograma del Evento</h1>
  ${
    items.length === 0
      ? '<p class="empty">No hay actividades en el cronograma.</p>'
      : `<table>
          <thead><tr><th>Hora</th><th>Actividad</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>`
  }
  <script>
    window.addEventListener('load', () => {
      window.focus();
      window.print();
    });
    window.addEventListener('afterprint', () => window.close());
  </script>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) {
      toast({
        title: 'No se pudo abrir la ventana de impresión',
        description: 'Permite las ventanas emergentes para imprimir el cronograma.',
        variant: 'destructive',
      });
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleExportCsv = () => {
    const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
    const rows = [
      ['Hora', 'Actividad'],
      ...items.map((item) => [item.hora, item.actividad]),
    ];
    const csv = '﻿' + rows.map((r) => r.map(escape).join(',')).join('\r\n');
    const baseName = event?.nombre
      ? event.nombre.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      : 'cronograma';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cronograma-${baseName || 'evento'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Calculate estimated duration
  const calculateDuration = () => {
    if (items.length < 2) return 'No definida';
    
    try {
      const firstTime = items[0]?.hora || '';
      const lastTime = items[items.length - 1]?.hora || '';
      
      if (!firstTime || !lastTime) return 'No definida';
      
      const [firstHour, firstMinute] = firstTime.split(':').map(Number);
      const [lastHour, lastMinute] = lastTime.split(':').map(Number);
      
      let hourDiff = lastHour - firstHour;
      let minuteDiff = lastMinute - firstMinute;
      
      if (minuteDiff < 0) {
        hourDiff--;
        minuteDiff += 60;
      }
      
      return `${hourDiff} horas ${minuteDiff > 0 ? `${minuteDiff} minutos` : ''}`;
    } catch (e) {
      return 'No definida';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Cronograma del Evento</h1>
            {isLoadingEvent ? (
              <Skeleton className="h-5 w-64 mt-1" />
            ) : (
              <p className="text-gray-600">
                {event?.nombre || 'Cargando...'} - {event?.fecha ? formatEventDate(event.fecha) : ''}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="tertiary" onClick={handlePrint}>
            <FileText className="w-4 h-4 mr-2" />
            Imprimir Horario
          </Button>
          <Button variant="outline" onClick={handleExportCsv} disabled={items.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Horario principal */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                Cronograma del Día
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item) => (
                    <ScheduleItem
                      key={item.id}
                      item={item}
                      onDelete={removeActivity}
                      onEdit={handleEditItem}
                    />
                  ))}

                  {/* Formulario inline para agregar nueva actividad */}
                  <div className="flex items-center space-x-4 p-3 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                    <div className="flex-shrink-0 w-3">
                      <Plus className="w-3 h-5 text-gray-400" />
                    </div>
                    <div className="flex-shrink-0 w-17">
                      <Input
                        type="time"
                        value={newActivity.hora}
                        onChange={(e) => setNewActivity({ ...newActivity, hora: e.target.value })}
                        className="text-xs h-8"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="Nueva actividad..."
                        value={newActivity.actividad}
                        onChange={(e) => setNewActivity({ ...newActivity, actividad: e.target.value })}
                        className="text-sm h-8"
                        onKeyPress={(e) => e.key === 'Enter' && addActivity()}
                      />
                    </div>
                    <div className="flex-shrink-0">
                      <Button
                        size="sm"
                        variant='secondary'
                        onClick={addActivity}
                        disabled={!canWrite || !newActivity.hora || !newActivity.actividad || isLoading}
                        className="h-8"
                      >
                        Agregar
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Panel lateral */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Actividades</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i}>
                      <Skeleton className="h-4 w-24 mb-1" />
                      <Skeleton className="h-5 w-32" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <span className="text-sm text-gray-600">Duración estimada</span>
                    <p className="font-semibold">{calculateDuration()}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Total actividades</span>
                    <p className="font-semibold">{items.length}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Hora inicio</span>
                    <p className="font-semibold">
                      {items.length > 0 ? items[0]?.hora : 'No definida'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Hora fin estimada</span>
                    <p className="font-semibold">
                      {items.length > 0 ? items[items.length - 1]?.hora : 'No definida'}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      </div>

      {/* Edit Modal */}
      <EditScheduleItemModal
        isOpen={!!editingItem}
        onClose={() => setEditingItem(null)}
        onSave={handleSaveEdit}
        item={editingItem}
        isLoading={isLoading}
      />
    </div>
  );
};
